import type {
  IAssetCategory,
  IAssetClassification,
  IAssetRatingSeverity,
  IAssetScoreField,
} from '@/lib/constants/assetManager'
import { ASSET_CATEGORIES, ASSET_CLASSIFICATIONS, ASSET_RATING_SEVERITIES } from '@/lib/constants/assetManager'

export const ASSET_MANAGER = {
  TABLE: {
    TITLE: 'Asset Inventory',
    SEARCH_PLACEHOLDER: 'Search by ID, asset or responsible',
    NO_RESPONSIBLE: 'Unassigned',
  },
  ACTIONS: {
    BULK_UPLOAD: 'Bulk upload',
    NEW_ASSET: 'New asset',
    APPLY: 'Apply',
  },
  TABLE_HEADER: {
    ID: 'ID',
    ASSET: 'Asset',
    CATEGORY: 'Category',
    RESPONSIBLE: 'Responsible',
    LOCATION: 'Location',
    RATING: 'Rating',
    CLASSIFICATION: 'Classification',
  },
  FILTERS: {
    ALL: 'All',
    FILTER_BY_CATEGORY: 'Filter by category',
    FILTER_BY_RESPONSIBLE: 'Filter by responsible',
    FILTER_BY_CLASSIFICATION: 'Filter by classification',
  },
  CREATION: {
    TITLE: 'New asset',
    NAME: 'Name',
    CREATE: 'Create asset',
    CANCEL: 'Cancel',
    ERROR_MESSAGE: 'Fill in every field before creating the asset',
    SUCCESS_MESSAGE: (assetCode: string) => `Asset ${assetCode} created`,
  },
  BULK_UPLOAD: {
    TITLE: 'Import assets from CSV',
    DESCRIPTION:
      'Upload a CSV file to import assets in bulk. Every row must be valid: if any row has errors, nothing is created and the errors are listed below.',
    UPLOAD_FILE: 'Upload CSV',
    UPLOADING: 'Uploading...',
    DOWNLOAD_SAMPLE: 'Download sample CSV',
    CANCEL: 'Cancel',
    SUCCESS: 'Assets imported successfully',
    RELOAD_ERROR: 'Assets were imported, but the list could not be refreshed. Reload the page to see them.',
    VALIDATION_TABLE_ROW: 'Row',
    VALIDATION_TABLE_FIELD: 'Field',
    VALIDATION_TABLE_ERROR: 'Error',
    VALIDATION_TABLE_RECEIVED_VALUE: 'Received value',
    HEADER_ERROR_TITLE: 'Your CSV header has problems',
    HEADER_ERROR_DESCRIPTION:
      'Fix the header row in your file and upload it again. The accepted columns are listed below.',
    ACCEPTED_COLUMNS_REQUIRED: 'Required columns',
    ACCEPTED_COLUMNS_OPTIONAL: 'Optional columns',
    SUMMARY_LINE: '{totalRows} rows checked, {errorCount} errors found. Fix them in your CSV and upload it again.',
    ERROR_UNKNOWN_COLUMN: 'Unknown column: {field}',
    ERROR_MISSING_REQUIRED_COLUMN: 'Required column missing: {field}',
    ERROR_EMPTY_FILE: 'The file has a header but no asset rows',
    ERROR_MALFORMED_ROW: 'The number of values in this row does not match the header',
    ERROR_REQUIRED: '{field} is required',
    ERROR_TOO_LONG: '{field} must be 255 characters or fewer',
    ERROR_INVALID_SCORE: '{field} must be a whole number from 1 to 5. Got "{receivedValue}"',
    ERROR_INVALID_OPTIONAL_SCORE:
      '{field} must be a whole number from 1 to 5, or empty if not applicable. Got "{receivedValue}"',
    ERROR_INVALID_CATEGORY: 'Invalid category "{receivedValue}". Allowed values: {allowedValues}',
    ERROR_INVALID_CLASSIFICATION: 'Invalid classification "{receivedValue}". Allowed values: {allowedValues}',
    ERROR_DEPARTMENT_NOT_FOUND:
      'Department not found in this organization: "{receivedValue}". Available departments: {allowedValues}',
    ERROR_DUPLICATE_IN_FILE: 'Duplicate asset name in this file: "{receivedValue}"',
    ERROR_DUPLICATE_IN_DB: 'An asset with this name already exists: "{receivedValue}"',
  },
  DRAWER: {
    DESCRIPTION: 'Description',
    ATTRIBUTES: 'Attributes',
    CATEGORIZATION: 'Asset categorization',
    FINAL_RATING: 'Final rating',
    FINAL_RATING_HINT: 'Average of the applicable dimensions (C, I, D, A, T)',
    NOTES: 'Notes',
    NOT_APPLICABLE: 'Not applicable',
    QUANTITY_SCOPE: 'Quantity / Scope',
    NO_DESCRIPTION: 'No description',
    NO_NOTES: 'No notes',
    UPDATE_ERROR: 'Failed to update asset',
    DELETE_SUCCESS: 'Asset deleted successfully',
    DELETE_ERROR: 'Failed to delete asset',
    SELECT_CATEGORY: 'Select a category',
    SELECT_RESPONSIBLE: 'Select a department',
    SEARCH_CATEGORIES: 'Search categories...',
    SEARCH_RESPONSIBLES: 'Search departments...',
    NO_CATEGORY_FOUND: 'No category found.',
    NO_RESPONSIBLE_FOUND: 'No department found.',
    DELETE_DIALOG: {
      TITLE: 'Delete Asset',
      QUESTION: 'Are you sure you want to delete the asset ',
      CANCEL: 'Cancel',
      DELETE: 'Delete',
    },
  },
  SCORES: {
    confidentialityScore: 'Confidentiality',
    integrityScore: 'Integrity',
    availabilityScore: 'Availability',
    authenticityScore: 'Authenticity',
    traceabilityScore: 'Traceability',
  } as Record<IAssetScoreField, string>,
  CATEGORIES: {
    [ASSET_CATEGORIES.BUSINESS_PROCESS]: 'Business processes and activities',
    [ASSET_CATEGORIES.INFORMATION]: 'Information',
    [ASSET_CATEGORIES.HARDWARE]: 'Hardware',
    [ASSET_CATEGORIES.SOFTWARE]: 'Software',
    [ASSET_CATEGORIES.NETWORK_COMMUNICATIONS]: 'Network and communications',
    [ASSET_CATEGORIES.PERSONNEL]: 'Personnel',
    [ASSET_CATEGORIES.PHYSICAL_FACILITIES]: 'Physical facilities',
    [ASSET_CATEGORIES.SUPPLIERS]: 'Suppliers',
  } as Record<IAssetCategory, string>,
  CLASSIFICATIONS: {
    [ASSET_CLASSIFICATIONS.INTERNAL]: 'Internal',
    [ASSET_CLASSIFICATIONS.PUBLIC]: 'Public',
    [ASSET_CLASSIFICATIONS.CONFIDENTIAL]: 'Confidential',
  } as Record<IAssetClassification, string>,
  RATING_SEVERITIES: {
    [ASSET_RATING_SEVERITIES.CRITICAL]: 'Critical',
    [ASSET_RATING_SEVERITIES.HIGH]: 'High',
    [ASSET_RATING_SEVERITIES.MEDIUM]: 'Medium',
    [ASSET_RATING_SEVERITIES.LOW]: 'Low',
    [ASSET_RATING_SEVERITIES.VERY_LOW]: 'Very low',
  } as Record<IAssetRatingSeverity, string>,
}
